import type { DocDetail, DocSummary } from "@xop/dashboard-shared";

import { dashboardPublicOrigin, triggerMailto } from "../../components/share/appShareLinks.js";
import { env } from "../../lib/env.js";

// Re-export the shared mail-opener so existing importers (and the unit test)
// keep `import { triggerMailto } from "./shareLinks.js"` — there is ONE
// `triggerMailto` implementation, in components/share/appShareLinks.ts.
export { triggerMailto };

/**
 * Share-link helpers for a documentation file. Pure (the only runtime read is
 * `env.supportWebUrl`, baked at build time) so the URL/encoding logic is
 * unit-tested in `shareLinks.test.ts`, and so BOTH the reader (`LibraryDocPage`)
 * and the list kebab (`RowActions`) build the SAME links — the one source of the
 * "Copy link" / "Share via email" targets. See docs/documentation.md.
 *
 * Two DISTINCT link shapes, by destination:
 *  - the **in-app staff deep-link** (`documentDeepLink`) for "Share to chat" —
 *    an internal `/documentation/library/:slug` route that only staff can open,
 *    matched by the chat applink allowlist.
 *  - the **public customer-portal URL** (`documentAbsoluteUrl`) for email +
 *    "Copy link" — these LEAVE the app, so they MUST be a stable, externally
 *    reachable address, never `window.location.origin` (that is the STAFF host:
 *    `localhost` in dev, `tauri://` on desktop, `crm.xopnetworks.com` in prod —
 *    none of which an email recipient can open). This was the "share emails a
 *    localhost link" bug.
 */

/** The minimal slice every share helper needs — a `DocSummary` or `DocDetail`
 * both satisfy it, so list rows and the reader call these identically. */
export type ShareableDoc = Pick<DocSummary, "slug" | "title"> & Partial<DocDetail>;

/** Known-good production portal origin — the loopback-proof fallback for any
 * link that leaves the app, mirroring portal-api's `Frontend::DEFAULT_PORTAL_HOST`
 * and the CRM's `PUBLIC_ASSET_HOST`. */
const PUBLIC_PORTAL_FALLBACK = "https://support.xopnetworks.com";

/** Origin (scheme + host) of the customer-facing support portal, with no
 * trailing slash. Trusts a configured `VITE_SUPPORT_CENTER_WEB_URL` ONLY when it
 * is present and non-loopback (the prod web/desktop build sets
 * `https://support.xopnetworks.com`); a blank/localhost/`127.0.0.1` value — the dev
 * default — is ignored in favour of the public host, exactly the rule
 * `Frontend.base_url` uses on the portal side. So an externally-shared link is
 * never built against a host only the sender's machine can reach. */
export function publicPortalOrigin(): string {
  const raw = (env.supportWebUrl ?? "").trim();
  const usable = raw !== "" && !raw.includes("localhost") && !raw.includes("127.0.0.1");
  return (usable ? raw : PUBLIC_PORTAL_FALLBACK).replace(/\/+$/, "");
}

/** In-app deep-link PATH to a doc's STAFF reader. This is the value posted into
 * chat (it must match the `documentation/library` entry in the chat applink
 * allowlist — `APP_LINK_SRC` in packages/shared/src/chat.ts). Internal-only. */
export function documentDeepLink(doc: Pick<ShareableDoc, "slug">): string {
  return `/documentation/library/${doc.slug}`;
}

/** PUBLIC, customer-facing reader PATH for a doc — the customer portal documentation
 * article route (`apps/portal-web` `ArticlePage`). Matches portal-api's
 * `DocumentPayload.portal_url_for`, the one URL an external recipient can open. */
export function documentPortalPath(doc: Pick<ShareableDoc, "slug">): string {
  return `/portal/knowledge-base/${doc.slug}`;
}

/** Absolute PUBLIC URL to a doc's customer-portal reader, for "Copy link" + the
 * email body. Built from {@link publicPortalOrigin}, never `window.location` —
 * so the same link is produced from the web app, the desktop shell, and dev. */
export function documentAbsoluteUrl(doc: Pick<ShareableDoc, "slug">): string {
  return `${publicPortalOrigin()}${documentPortalPath(doc)}`;
}

/** `mailto:` href that opens the user's mail client prefilled with the doc
 * title as the subject and a short body carrying the absolute link. */
export function documentMailtoHref(doc: ShareableDoc): string {
  const subject = doc.title || "Document";
  const body = `Sharing a document: ${doc.title || doc.slug}\n\n${documentAbsoluteUrl(doc)}`;
  return `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

/** A documentation FOLDER (or sub-folder) — what "Share folder" links to. */
export interface ShareableFolder {
  slug: string;
  name: string;
  /** Only a folder ticked for the customer portal gets a customer link. */
  visible_in_customer_portal?: boolean;
}

/** Absolute STAFF link straight to a folder's page in the command center
 * (`/documentation/type/:slug`). Built from the public dashboard host, never
 * `window.location` — the desktop app's origin is `tauri://`. */
export function folderStaffUrl(folder: Pick<ShareableFolder, "slug">): string {
  return `${dashboardPublicOrigin()}/documentation/type/${encodeURIComponent(folder.slug)}`;
}

/** Absolute CUSTOMER-portal link to a folder — portal-web's
 * `knowledge-base/category/:slug` route. Only meaningful when the folder is
 * published to the customer portal; the caller decides whether to offer it. */
export function folderPortalUrl(folder: Pick<ShareableFolder, "slug">): string {
  return `${publicPortalOrigin()}/portal/knowledge-base/category/${encodeURIComponent(folder.slug)}`;
}

/** `mailto:` for a folder: the staff link always, the customer link when the
 * folder is published to customers. */
export function folderMailtoHref(folder: ShareableFolder): string {
  const lines = [`Sharing a documentation folder: ${folder.name}`, "", folderStaffUrl(folder)];
  if (folder.visible_in_customer_portal) lines.push("", `Customer portal: ${folderPortalUrl(folder)}`);
  return `mailto:?subject=${encodeURIComponent(folder.name)}&body=${encodeURIComponent(lines.join("\n"))}`;
}
